import styled, { css } from 'styled-components';

import { COLORS, MOBILE, SPACE } from '../../theme/tokens';
import { Text } from '../../components';

/**
 * Page-level layout for the Mailbox section body: the panel heading, then the tile grid.
 * The surrounding chrome (PageShell, BackHeader, the content cap) comes from the Settings section
 * dispatcher, so this owns only the vertical stack. Values from the Figma card spec sheet
 * (OWS-Flow 46:877): 2 columns at a 20px gutter, one column under MOBILE.
 */
export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[20]};
`;

/** The panel heading row: the count on the left, the billing split and the domain on the right.
 *  Wraps under MOBILE so the right-hand meta drops below the count instead of squeezing it. */
export const Heading = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACE[12]};
  flex-wrap: wrap;
  padding-bottom: ${SPACE[20]};
`;

/** The section's one banner position, under the heading: the same 20px to the grid below as the
 *  heading's own bottom padding above it. */
export const BannerSlot = styled.div`
  padding-bottom: ${SPACE[20]};
`;

// TEXT COLOUR, ONE RULE FOR THE WHOLE SCENARIO: one strong colour per block. The thing being named
// (a title, an address, a value) is COLORS.textStrong; everything explaining it (a description, a
// sub-line, a body paragraph, a hint, a tooltip) is COLORS.textMuted, or textSubtle only where that
// is already the neighbouring convention (the add tiles, the checkout sheet's name line). No third
// body colour. If two lines in the same block are both textStrong, one of them is wrong. The Home
// Spotlight cards follow the same rule, so a card there and a tile here describe things alike.

/** "2 mailboxes" / "No mailboxes yet": the panel's title (Figma: 18px bold, 27px line). */
export const Count = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  line-height: 27px;
  color: ${COLORS.textStrong};
`;

/** "1 free, 1 paid · domain": quiet, right-aligned. `Meta` is the row; the two spans inside it
 *  are the split and the domain, separated by the middle dot. */
export const Meta = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: ${SPACE[8]};
  font-size: 13px;
  line-height: 20px;
  color: ${COLORS.textMuted};
  min-width: 0;
`;

/** The domain, truncating rather than wrapping a long one at phone width. */
export const MetaDomain = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * The tile grid: the add card first, then one tile per mailbox. `align-items: start` on
 * purpose: the default `stretch` would equalise sibling heights for free and hide a tile whose own
 * skeleton is not fixed. Every tile is the same height BY ITS OWN CONSTRUCTION (fixed head, sub-line
 * and footer slots), and the CT test measures that with this grid not stretching it.
 *
 * `$single`: no mailbox card to show, so the add tile is alone. It takes the one-column track MOBILE
 * already uses, filling the row instead of leaving an empty cell beside it.
 */
const ONE_COLUMN = css`
  grid-template-columns: minmax(0, 1fr);
`;

export const Grid = styled.div<{ $single?: boolean }>`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${SPACE[20]};
  align-items: start;

  ${MOBILE} {
    ${ONE_COLUMN}
  }

  ${({ $single }) => ($single ? ONE_COLUMN : null)}
`;
